import * as types from "./mutation-types";
import cloneDeep from "lodash.clonedeep";
import { getDateString } from "@/util";

const ReportConstant = {
    COVER_PAGE: "CoverPage",
    Investment_Details: "InvestmentDetail"
};

export const putOrPostHypo = ({ commit }, { fetch, hypoInput }) => {
    const { hypoId } = hypoInput;
    if (hypoId) {
        return fetch({
            url: `/api/v1/Hypo/${hypoId}`,
            method: "PUT",
            body: JSON.stringify(hypoInput)
        });
    }
    return fetch({
        url: "/api/v1/Hypo",
        method: "POST",
        body: JSON.stringify(hypoInput)
    });
};

export const saveHypo = ({ state, commit, dispatch, rootGetters }, { fetch }) => {
    const hypoInput = rootGetters.hypoInput;
    return dispatch("putOrPostHypo", { fetch, hypoInput }).then(data => {
        if (!hypoInput.hypoId) {
            commit(`hypo/${types.SET_HYPO_ID}`, data.entityId);
        }
        commit(`investments/${types.UPDATE_PORTFOLIOLIST}`, hypoInput.portfolioList);
        commit(`investments/${types.UPDATE_RAW_PORTFOLIOLIST}`, hypoInput.portfolioList);
        dispatch("saveInitialHypoData");
    });
};

const getValidateAssumptionResult = (portfolioList, errorInfos = []) => {
    return portfolioList.map((portfolio, index) => {
        const invalidPortfolio = errorInfos.find(item => item.id === portfolio.id);
        const { benchmarkValid, rebalanceValid } = invalidPortfolio || { benchmarkValid: true, rebalanceValid: true };
        return {
            index,
            valid: benchmarkValid !== false && rebalanceValid !== false
        };
    });
};

function getErrorSecurities(portfolioList, errorInfos = []) {
    return portfolioList.map((portfolio, index) => {
        const invalidPortfolio = errorInfos.find(item => item.id === portfolio.id);
        const { errorSecurities = [] } = invalidPortfolio || { errorSecurities: [] };
        return {
            index,
            errorSecurities
        };
    });
}

const isUnMatchedPreset = (presetTimePeriod, presetTimePeriodOptions) => {
    const { type, startDate, endDate } = presetTimePeriod;
    const pStartDate = getDateString(new Date(startDate));
    const pEndDate = getDateString(new Date(endDate));
    return (
        presetTimePeriodOptions.find(item => {
            return item.type === type && pStartDate === getDateString(new Date(item.startDate)) && pEndDate === getDateString(new Date(item.endDate));
        }) === undefined
    );
};

//adjust none exist preset time period
const adjustSecurityTimePeriod = (checkedData, presetTimePeriodOptions = []) => {
    const { portfolioList } = checkedData.hypoInput;
    checkedData.hypoInput.portfolioList = portfolioList.map(portfolio => {
        const securities = portfolio.securities.map(security => {
            let needAdjustPreset = false;
            const { timePeriod, presetTimePeriod } = security;
            if (timePeriod === 99 && isUnMatchedPreset(presetTimePeriod, presetTimePeriodOptions)) {
                needAdjustPreset = true;
            }
            if (needAdjustPreset) {
                const newSecurity = { ...security };
                delete newSecurity.presetTimePeriod;
                return {
                    ...newSecurity,
                    timePeriod: 0
                };
            }
            return security;
        });
        return {
            ...portfolio,
            securities
        };
    });
    return checkedData;
};
const checkPortfolioSync = portfolioList => {
    // For old Hypos (before sync feature)
    const updatedPortfolio = portfolioList[1];
    updatedPortfolio.synced = updatedPortfolio.synced || false;
    updatedPortfolio.firstSynced = updatedPortfolio.firstSynced || true;
    return [portfolioList[0], updatedPortfolio];
};
const cleanBenchmarks = portfolioList => {
    const ignoredProps = ["position", "margin"];
    return portfolioList.map(portfolio => {
        const benchmark = { ...portfolio.benchmark };
        if (benchmark && benchmark.benchmarkHoldings) {
            benchmark.benchmarkHoldings = benchmark.benchmarkHoldings.map(holding => {
                ignoredProps.forEach(prop => delete holding[prop]);
                return holding;
            });
        }
        return {
            ...portfolio,
            benchmark
        };
    });
};

const compatiblePortfolioFees = portfolioList => {
    return portfolioList.map(portfolio => {
        const { feeSetting } = portfolio;
        if (feeSetting && typeof feeSetting.redemptionCharge === "number") {
            feeSetting.redemptionCharge = feeSetting.redemptionCharge === 1;
            return {
                ...portfolio,
                feeSetting
            };
        }
        return portfolio;
    });
};

const adjustPortfolioList = checkedData => {
    const { hypoInput } = checkedData;
    let { portfolioList, hypoType } = hypoInput;
    portfolioList = compatiblePortfolioFees(portfolioList); //HYPO-5005, fix saved dirty data before data structure changed.
    portfolioList = cleanBenchmarks(portfolioList);
    if (hypoType === 1 && portfolioList.length === 2) {
        portfolioList = checkPortfolioSync(portfolioList);
    }
    return {
        ...checkedData,
        hypoInput: {
            ...hypoInput,
            portfolioList
        }
    };
};

export const fetchHypo = ({ commit }, { fetch, id }) => {
    return fetch({
        url: `/api/v1/Hypo/${id}`,
        method: "GET"
    });
};

export const getHypo = ({ commit, state, dispatch, rootGetters }, { fetch, id }) => {
    return dispatch("fetchHypo", { fetch, id }).then(checkedData => {
        checkedData = adjustSecurityTimePeriod(checkedData, rootGetters["config/presetTimePeriodOptions"]);
        checkedData = adjustPortfolioList(checkedData);
        const data = checkedData.hypoInput;
        commit(`hypo/${types.SET_HYPO_ID}`, data.hypoId);
        commit(`hypo/${types.SET_HYPO_NAME}`, data.hypoName);
        commit(`hypo/${types.SET_HYPO_TYPE}`, data.hypoType);
        if (data.globalSetting.assetGroups) {
            commit(`hypo/${types.SET_ASSET_ALLOCATION}`, data.globalSetting.assetGroups);
        }
        commit(`hypo/${types.SET_HYPO_LAST_COMPLETED_STEP}`, data.lastCompletedStep);

        const { errorInfos } = checkedData;
        const portfolioList = data.portfolioList;
        if (portfolioList.length === 0) {
            dispatch("investments/setPortfoliosByFlow");
        } else {
            commit(`investments/${types.UPDATE_PORTFOLIOLIST}`, portfolioList);
            commit(`investments/${types.UPDATE_RAW_PORTFOLIOLIST}`, portfolioList);
        }

        const coverPageReport = data.reportSetting.reportOptions.find(report => report.reportId.toLowerCase() === ReportConstant.COVER_PAGE.toLowerCase());
        const idrReport = data.reportSetting.reportOptions.find(report => report.reportId.toLowerCase() === ReportConstant.Investment_Details.toLowerCase());
        if (coverPageReport !== undefined) {
            const coverPageSetting = coverPageReport.settings;
            commit(`reportSetting/${types.SET_REPORT_TITLE}`, coverPageSetting.reportTitle);
            commit(`reportSetting/${types.SET_PREPARED_FOR_FIRST_NAME}`, coverPageSetting.preparedForFirstName);
            commit(`reportSetting/${types.SET_PREPARED_FOR_LAST_NAME}`, coverPageSetting.preparedForLastName);
            commit(`reportSetting/${types.SET_PREPARED_FOR_ADDRESS}`, coverPageSetting.preparedForAddress);
            commit(`reportSetting/${types.SET_PREPARED_FOR_CITY}`, coverPageSetting.preparedForCity);
            commit(`reportSetting/${types.SET_PREPARED_FOR_STATE}`, coverPageSetting.preparedForState);
            commit(`reportSetting/${types.SET_PREPARED_FOR_COUNTRY}`, coverPageSetting.preparedForCountry);
            commit(`reportSetting/${types.SET_PREPARED_FOR_ZIP_CODE}`, coverPageSetting.preparedForZipCode);
            commit(`reportSetting/${types.SET_PREPARED_FOR_PHONE}`, coverPageSetting.preparedForPhone);
            commit(`reportSetting/${types.SET_PREPARED_FOR_EMAIL}`, coverPageSetting.preparedForEmail);
            commit(`reportSetting/${types.SET_PREPARED_BY_FIRST_NAME}`, coverPageSetting.preparedByFirstName);
            commit(`reportSetting/${types.SET_PREPARED_BY_LAST_NAME}`, coverPageSetting.preparedByLastName);
            commit(`reportSetting/${types.SET_PREPARED_BY_ADDRESS}`, coverPageSetting.preparedByAddress);
            commit(`reportSetting/${types.SET_PREPARED_BY_CITY}`, coverPageSetting.preparedByCity);
            commit(`reportSetting/${types.SET_PREPARED_BY_STATE}`, coverPageSetting.preparedByState);
            commit(`reportSetting/${types.SET_PREPARED_BY_COUNTRY}`, coverPageSetting.preparedByCountry);
            commit(`reportSetting/${types.SET_PREPARED_BY_ZIP_CODE}`, coverPageSetting.preparedByZipCode);
            commit(`reportSetting/${types.SET_PREPARED_BY_PHONE}`, coverPageSetting.preparedByPhone);
            commit(`reportSetting/${types.SET_PREPARED_BY_EMAIL}`, coverPageSetting.preparedByEmail);
        }
        if (idrReport && idrReport.settings) {
            commit(`reportSetting/${types.SET_INCLUDE_MF_COMPREHENSIVE}`, idrReport.settings.includeMFComprehensive);
        }

        const savedReportOptions = data.reportSetting.reportOptions.map(report => {
            return {
                reportId: report.reportId,
                include: report.include
            };
        });
        return dispatch("reportSetting/loadReportSetting", savedReportOptions)
            .then(() => {
                return dispatch("saveInitialHypoData");
            })
            .then(() => {
                return {
                    ...checkedData,
                    hypoInput: data,
                    errors: {
                        assumptions: getValidateAssumptionResult(portfolioList, errorInfos),
                        securities: getErrorSecurities(portfolioList, errorInfos)
                    }
                };
            });
    });
};

export const saveInitialHypoData = ({ commit, rootGetters }) => {
    const rawHypoInput = cloneDeep(rootGetters.hypoInput);
    commit(`hypo/${types.SET_RAW_HYPO_INPUT}`, rawHypoInput);
};

export const clearHypoInput = ({ state, commit, dispatch }) => {
    commit("hypo/RESET");
    commit("chart/RESET");
    commit("investments/RESET");
    commit("reportSetting/RESET");
    dispatch("hypo/updateStateByPreference");
    dispatch("reportSetting/setReportCoverSetting", state.preference.reportCover);
    dispatch("reportSetting/setReportGeneralSetting", state.preference.reportGeneral);
    return dispatch("reportSetting/loadReportSetting", state.preference.reportOptions);
};
